// Translations for Serbian (sr) and English (en)
export type Language = 'sr' | 'en';

export interface Translations {
  // Navigation
  nav: {
    home: string;
    offers: string;
    demands: string;
    auctions: string;
    blog: string;
    faq: string;
    about: string;
    login: string;
    register: string;
    logout: string;
    myListings: string;
    profile: string;
    admin: string;
    buyTokens: string;
    newListing: string;
  };
  // Common
  common: {
    search: string;
    filter: string;
    submit: string;
    cancel: string;
    save: string;
    delete: string;
    edit: string;
    close: string;
    loading: string;
    error: string;
    success: string;
    noResults: string;
    seeMore: string;
    seeLess: string;
    all: string;
    select: string;
    language: string;
    darkMode: string;
    lightMode: string;
    postAd: string;
  };
  // Home page
  home: {
    heroTitle: string;
    heroTitleHighlight: string;
    heroSubtitle: string;
    searchPlaceholder: string;
    propertyType: string;
    location: string;
    maxPrice: string;
    featured: string;
    latest: string;
    activeAuctions: string;
    viewAll: string;
    stats: {
      activeListings: string;
      happyUsers: string;
      successfulTransactions: string;
      yearsExperience: string;
    };
  };
  // Property
  property: {
    sale: string;
    rent: string;
    demand: string;
    auction: string;
    price: string;
    pricePerM2: string;
    pricePerAre: string;
    area: string;
    rooms: string;
    floor: string;
    heating: string;
    features: string;
    description: string;
    location: string;
    region: string;
    city: string;
    address: string;
    contact: string;
    views: string;
    favorites: string;
    addToFavorites: string;
    removeFromFavorites: string;
    placeBid: string;
    currentBid: string;
    auctionEnds: string;
    bidHistory: string;
    noBids: string;
    // Detail page
    notFound: string;
    backToListings: string;
    startingPrice: string;
    bidsCount: string;
    yourBid: string;
    minBid: string;
    submitBid: string;
    cancel: string;
    showPhone: string;
    sendEmail: string;
    published: string;
    sendMessage: string;
    scheduleVisit: string;
    similarListings: string;
    noMoreBids: string;
    beFirst: string;
    perMonth: string;
    plotArea: string;
  };
  // Filters
  filters: {
    title: string;
    category: string;
    priceRange: string;
    minPrice: string;
    maxPrice: string;
    areaRange: string;
    minArea: string;
    maxArea: string;
    rooms: string;
    anyRooms: string;
    heating: string;
    anyHeating: string;
    features: string;
    clearFilters: string;
    applyFilters: string;
    advancedFilters: string;
    locationSearch: string;
    locationPlaceholder: string;
    selectAllRegions: string;
    deselectAllRegions: string;
  };
  // Categories
  categories: {
    house: string;
    apartment: string;
    commercial: string;
    land: string;
    garage: string;
  };
  // Subscription popup
  subscription: {
    title: string;
    subtitle: string;
    emailPlaceholder: string;
    subscribe: string;
    dismiss: string;
    success: string;
    alreadySubscribed: string;
    invalidEmail: string;
    privacyNote: string;
  };
  // AI Assistant
  ai: {
    title: string;
    subtitle: string;
    placeholder: string;
    send: string;
    thinking: string;
    disclaimer: string;
    legalDisclaimer: string;
    priceInfo: string;
    documentCheck: string;
    suspiciousWarning: string;
    prosAndCons: string;
    reportAbuse: string;
    abuseReported: string;
  };
  // Blog
  blog: {
    title: string;
    subtitle: string;
    readMore: string;
    publishedOn: string;
    author: string;
    categories: string;
    recentPosts: string;
  };
  // FAQ
  faq: {
    title: string;
    subtitle: string;
    searchPlaceholder: string;
    noResults: string;
  };
  // About
  about: {
    title: string;
    subtitle: string;
    mission: string;
    missionText: string;
    vision: string;
    visionText: string;
    values: string;
    team: string;
    contact: string;
  };
  // Auth
  auth: {
    email: string;
    password: string;
    confirmPassword: string;
    name: string;
    phone: string;
    loginTitle: string;
    registerTitle: string;
    forgotPassword: string;
    noAccount: string;
    hasAccount: string;
    loginButton: string;
    registerButton: string;
    invalidCredentials: string;
    emailExists: string;
    passwordMismatch: string;
  };
  // Errors
  errors: {
    generic: string;
    network: string;
    unauthorized: string;
    notFound: string;
    validation: string;
    serverError: string;
  };
  // Notifications
  notifications: {
    title: string;
    noNotifications: string;
    markAsRead: string;
    newMatch: string;
    expiringListing: string;
    newBid: string;
    outbid: string;
  };
  // Footer
  footer: {
    description: string;
    links: string;
    legal: string;
    terms: string;
    privacy: string;
    contact: string;
    copyright: string;
  };
  // Share
  share: {
    title: string;
    copyLink: string;
    copied: string;
    shareVia: string;
    shareAnywhere: string;
    copiesLink: string;
  };
  // Demands
  demands: {
    title: string;
    subtitle: string;
    haveProperty: string;
    contactDirectly: string;
    addListing: string;
    noDemands: string;
    tryChanging: string;
    postDemand: string;
    buying: string;
    renting: string;
    budgetUpTo: string;
    call: string;
    sendMessage: string;
    upTo: string;
    rooms: string;
    onlyLoggedIn: string;
    loginToSee: string;
  };
  // Auctions
  auctions: {
    title: string;
    subtitle: string;
    placeBid: string;
    currentBid: string;
    auctionEnds: string;
    sendMessage: string;
    onlyVerified: string;
    verifyEmail: string;
  };
  // Contact visibility
  contact: {
    onlyLoggedIn: string;
    onlyVerified: string;
    loginRequired: string;
    verificationRequired: string;
  };
}

export const translations: Record<Language, Translations> = {
  sr: {
    nav: {
      home: 'Početna',
      offers: 'Ponuda',
      demands: 'Potražnja',
      auctions: 'Aukcije',
      blog: 'Blog',
      faq: 'Česta pitanja',
      about: 'O nama',
      login: 'Prijava',
      register: 'Registracija',
      logout: 'Odjava',
      myListings: 'Moji oglasi',
      profile: 'Profil',
      admin: 'Admin',
      buyTokens: 'Kupi tokene',
      newListing: 'Novi oglas',
    },
    common: {
      search: 'Pretraži',
      filter: 'Filter',
      submit: 'Pošalji',
      cancel: 'Otkaži',
      save: 'Sačuvaj',
      delete: 'Obriši',
      edit: 'Izmeni',
      close: 'Zatvori',
      loading: 'Učitavanje...',
      error: 'Greška',
      success: 'Uspešno',
      noResults: 'Nema rezultata',
      seeMore: 'Prikaži više',
      seeLess: 'Prikaži manje',
      all: 'Sve',
      select: 'Izaberi',
      language: 'Jezik',
      darkMode: 'Tamni režim',
      lightMode: 'Svetli režim',
      postAd: 'Postavi oglas',
    },
    home: {
      heroTitle: 'Pronađite svoju',
      heroTitleHighlight: 'idealnu nekretninu',
      heroSubtitle: 'Pretražite hiljade oglasa za prodaju i izdavanje. Ponuda, potražnja i aukcije na jednom mestu.',
      searchPlaceholder: 'Pretražite po adresi ili lokaciji...',
      propertyType: 'Tip nekretnine',
      location: 'Lokacija',
      maxPrice: 'Max cena (EUR)',
      featured: 'Istaknuto',
      latest: 'Najnovije nekretnine',
      activeAuctions: 'Aktivne aukcije',
      viewAll: 'Pogledaj sve',
      stats: {
        activeListings: 'Aktivnih oglasa',
        happyUsers: 'Zadovoljnih korisnika',
        successfulTransactions: 'Uspešnih transakcija',
        yearsExperience: 'Godina iskustva',
      },
    },
    property: {
      sale: 'Prodaja',
      rent: 'Izdavanje',
      demand: 'Potražnja',
      auction: 'Aukcija',
      price: 'Cena',
      pricePerM2: 'Cena po m²',
      pricePerAre: 'Cena po aru',
      area: 'Površina',
      rooms: 'Sobe',
      floor: 'Sprat',
      heating: 'Grejanje',
      features: 'Karakteristike',
      description: 'Opis',
      location: 'Lokacija',
      region: 'Region',
      city: 'Podregion',
      address: 'Adresa',
      contact: 'Kontakt',
      views: 'Pregleda',
      favorites: 'Omiljeno',
      addToFavorites: 'Dodaj u omiljene',
      removeFromFavorites: 'Ukloni iz omiljenih',
      placeBid: 'Licitiraj',
      currentBid: 'Trenutna ponuda',
      auctionEnds: 'Aukcija završava',
      bidHistory: 'Istorija ponuda',
      noBids: 'Nema ponuda',
      // Detail page
      notFound: 'Oglas nije pronađen',
      backToListings: 'Nazad na ponudu',
      startingPrice: 'Početna cena',
      bidsCount: 'ponuda',
      yourBid: 'Vaša ponuda (EUR)',
      minBid: 'Min.',
      submitBid: 'Pošalji ponudu',
      cancel: 'Otkaži',
      showPhone: 'Prikaži telefon',
      sendEmail: 'Pošalji email',
      published: 'Objavljeno',
      sendMessage: 'Pošalji poruku',
      scheduleVisit: 'Zakaži obilazak',
      similarListings: 'Slični oglasi',
      noMoreBids: 'Još nema ponuda.',
      beFirst: 'Budite prvi!',
      perMonth: '/mes',
      plotArea: 'Plac',
    },
    filters: {
      title: 'Filteri',
      category: 'Kategorija',
      priceRange: 'Raspon cena',
      minPrice: 'Min cena',
      maxPrice: 'Max cena',
      areaRange: 'Raspon površine',
      minArea: 'Min površina',
      maxArea: 'Max površina',
      rooms: 'Broj soba',
      anyRooms: 'Bilo koji broj soba',
      heating: 'Grejanje',
      anyHeating: 'Bilo koje grejanje',
      features: 'Karakteristike',
      clearFilters: 'Očisti filtere',
      applyFilters: 'Primeni filtere',
      advancedFilters: 'Napredni filteri',
      locationSearch: 'Pretraga lokacije',
      locationPlaceholder: 'Unesite adresu ili oblast...',
      selectAllRegions: 'Označi sve regione',
      deselectAllRegions: 'Odznači sve regione',
    },
    categories: {
      house: 'Kuća',
      apartment: 'Stan',
      commercial: 'Poslovni prostor',
      land: 'Plac',
      garage: 'Garaža',
    },
    subscription: {
      title: 'Budite u toku sa najnovijim nekretninama',
      subtitle: 'Prijavite se na naš newsletter i prvi saznajte o novim oglasima koji odgovaraju vašim kriterijumima.',
      emailPlaceholder: 'Vaša email adresa',
      subscribe: 'Prijavi se',
      dismiss: 'Ne, hvala',
      success: 'Uspešno ste se prijavili na newsletter!',
      alreadySubscribed: 'Već ste prijavljeni na newsletter.',
      invalidEmail: 'Unesite validnu email adresu.',
      privacyNote: 'Vaši podaci su bezbedni i nećemo ih deliti sa trećim stranama.',
    },
    ai: {
      title: 'AI Pomoćnik',
      subtitle: 'Pitajte nas o cenama, dokumentaciji i nekretninama',
      placeholder: 'Postavite pitanje o nekretnini, cenama u regionu, dokumentaciji...',
      send: 'Pošalji',
      thinking: 'Razmišljam...',
      disclaimer: 'AI pomoćnik koristi OpenAI za generisanje odgovora. Odgovori su informativne prirode i ne predstavljaju pravni savet.',
      legalDisclaimer: 'Za pravne savete i stručnu dokumentaciju, konsultujte se sa advokatom ili notarom. Više informacija na paragraf.rs',
      priceInfo: 'Prosečna cena u ovom regionu',
      documentCheck: 'Dokumentacija koju treba proveriti',
      suspiciousWarning: 'Potencijalno sumnjivi elementi',
      prosAndCons: 'Prednosti i mane',
      reportAbuse: 'Prijavi zloupotrebu',
      abuseReported: 'Prijava poslata administratoru.',
    },
    blog: {
      title: 'Blog',
      subtitle: 'Korisni saveti i vesti iz sveta nekretnina',
      readMore: 'Pročitaj više',
      publishedOn: 'Objavljeno',
      author: 'Autor',
      categories: 'Kategorije',
      recentPosts: 'Najnoviji članci',
    },
    faq: {
      title: 'Česta pitanja',
      subtitle: 'Pronađite odgovore na najčešća pitanja o korišćenju platforme',
      searchPlaceholder: 'Pretražite pitanja...',
      noResults: 'Nema rezultata za vašu pretragu',
    },
    about: {
      title: 'O nama',
      subtitle: 'Svoj Na Svom je napravljen od strane posvećene i stručne grupe ljudi sa dugogodišnjim iskustvom u marketingu, prometu nepokretnosti i informacionim tehnologijama. Prikupili smo korisničke zahteve, želje i potrebe i napravili najbolji sajt za reklamiranje vaših potreba u prometu nepokretnosti.',
      mission: 'Naša misija',
      missionText: 'Povezivanje kupaca i prodavaca nekretnina na transparentan, siguran i efikasan način. Slobodno nas kontaktirajte za više informacija na kancelarija@svojnasvome.com ili +381 65 290 7002.',
      vision: 'Naša vizija',
      visionText: 'Postati vodeća platforma za promet nekretnina u Srbiji i regionu, sa najboljim korisničkim iskustvom i naprednim tehnološkim rešenjima.',
      values: 'Naše vrednosti',
      team: 'Naš tim',
      contact: 'Kontaktirajte nas',
    },
    auth: {
      email: 'Email',
      password: 'Lozinka',
      confirmPassword: 'Potvrdite lozinku',
      name: 'Ime i prezime',
      phone: 'Telefon',
      loginTitle: 'Prijava',
      registerTitle: 'Registracija',
      forgotPassword: 'Zaboravili ste lozinku?',
      noAccount: 'Nemate nalog?',
      hasAccount: 'Već imate nalog?',
      loginButton: 'Prijavi se',
      registerButton: 'Registruj se',
      invalidCredentials: 'Pogrešan email ili lozinka',
      emailExists: 'Email adresa je već registrovana',
      passwordMismatch: 'Lozinke se ne poklapaju',
    },
    errors: {
      generic: 'Došlo je do greške. Pokušajte ponovo.',
      network: 'Greška u mreži. Proverite vašu internet konekciju.',
      unauthorized: 'Nemate dozvolu za ovu akciju.',
      notFound: 'Stranica nije pronađena.',
      validation: 'Proverite unesene podatke.',
      serverError: 'Greška na serveru. Pokušajte kasnije.',
    },
    notifications: {
      title: 'Obaveštenja',
      noNotifications: 'Nema novih obaveštenja',
      markAsRead: 'Označi kao pročitano',
      newMatch: 'Novo poklapanje!',
      expiringListing: 'Oglas ističe uskoro',
      newBid: 'Nova ponuda na vašoj aukciji',
      outbid: 'Nadmašena je vaša ponuda',
    },
    footer: {
      description: 'Vaš pouzdani partner u pronalaženju idealnog doma. Portal za oglašavanje nekretnina u Srbiji.',
      links: 'Brzi linkovi',
      legal: 'Pravne informacije',
      terms: 'Uslovi korišćenja',
      privacy: 'Politika privatnosti',
      contact: 'Kontakt',
      copyright: '© 2026 Svoj Na Svom. Sva prava zadržana.',
    },
    // Deljenje
    share: {
      title: 'Podeli',
      copyLink: 'Kopiraj link',
      copied: 'Kopirano!',
      shareVia: 'Podeli putem',
      shareAnywhere: 'Podeli gde god želiš',
      copiesLink: 'Kopira link',
    },
    // Potražnja
    demands: {
      title: 'Potražnja nekretnina',
      subtitle: 'Pregledajte šta traže potencijalni kupci i zakupci',
      haveProperty: 'Imate nekretninu koja odgovara potražnji?',
      contactDirectly: 'Kontaktirajte oglašivača direktno i ponudite svoju nekretninu',
      addListing: 'Dodaj ponudu',
      noDemands: 'Nema potražnji za ove filtere',
      tryChanging: 'Pokušajte da promenite filtere ili objavite svoju potražnju',
      postDemand: 'Objavi potražnju',
      buying: 'Kupovina',
      renting: 'Iznajmljivanje',
      budgetUpTo: 'Budžet do:',
      call: 'Pozovi',
      sendMessage: 'Pošalji poruku',
      upTo: 'Do',
      rooms: 'soba',
      onlyLoggedIn: 'Samo prijavljeni korisnici mogu videti broj telefona',
      loginToSee: 'Prijavite se da vidite kontakt informacije',
    },
    // Aukcije
    auctions: {
      title: 'Aktivne aukcije',
      subtitle: 'Nekretnine koje se prodaju putem aukcije',
      placeBid: 'Licitiraj',
      currentBid: 'Trenutna ponuda',
      auctionEnds: 'Aukcija završava',
      sendMessage: 'Pošalji poruku',
      onlyVerified: 'Samo verifikovani korisnici mogu licitirati',
      verifyEmail: 'Molimo prvo verifikujte vašu email adresu',
    },
    // Vidljivost kontakta
    contact: {
      onlyLoggedIn: 'Samo prijavljeni korisnici mogu videti kontakt informacije',
      onlyVerified: 'Samo verifikovani korisnici mogu slati poruke i ponude',
      loginRequired: 'Potrebna prijava',
      verificationRequired: 'Potrebna verifikacija email adrese',
    },
  },
  en: {
    nav: {
      home: 'Home',
      offers: 'Listings',
      demands: 'Wanted',
      auctions: 'Auctions',
      blog: 'Blog',
      faq: 'FAQ',
      about: 'About Us',
      login: 'Login',
      register: 'Register',
      logout: 'Logout',
      myListings: 'My Listings',
      profile: 'Profile',
      admin: 'Admin',
      buyTokens: 'Buy Tokens',
      newListing: 'New Listing',
    },
    common: {
      search: 'Search',
      filter: 'Filter',
      submit: 'Submit',
      cancel: 'Cancel',
      save: 'Save',
      delete: 'Delete',
      edit: 'Edit',
      close: 'Close',
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      noResults: 'No results',
      seeMore: 'See more',
      seeLess: 'See less',
      all: 'All',
      select: 'Select',
      language: 'Language',
      darkMode: 'Dark mode',
      lightMode: 'Light mode',
      postAd: 'Post Ad',
    },
    home: {
      heroTitle: 'Find your',
      heroTitleHighlight: 'ideal property',
      heroSubtitle: 'Browse thousands of listings for sale and rent. Listings, wanted ads and auctions in one place.',
      searchPlaceholder: 'Search by address or location...',
      propertyType: 'Property type',
      location: 'Location',
      maxPrice: 'Max price (EUR)',
      featured: 'Featured',
      latest: 'Latest Properties',
      activeAuctions: 'Active Auctions',
      viewAll: 'View all',
      stats: {
        activeListings: 'Active Listings',
        happyUsers: 'Happy Users',
        successfulTransactions: 'Successful Transactions',
        yearsExperience: 'Years of Experience',
      },
    },
    property: {
      sale: 'For Sale',
      rent: 'For Rent',
      demand: 'Wanted',
      auction: 'Auction',
      price: 'Price',
      pricePerM2: 'Price per m²',
      pricePerAre: 'Price per are',
      area: 'Area',
      rooms: 'Rooms',
      floor: 'Floor',
      heating: 'Heating',
      features: 'Features',
      description: 'Description',
      location: 'Location',
      region: 'Region',
      city: 'Subregion',
      address: 'Address',
      contact: 'Contact',
      views: 'Views',
      favorites: 'Favorites',
      addToFavorites: 'Add to favorites',
      removeFromFavorites: 'Remove from favorites',
      placeBid: 'Place Bid',
      currentBid: 'Current Bid',
      auctionEnds: 'Auction Ends',
      bidHistory: 'Bid History',
      noBids: 'No bids yet',
      // Detail page
      notFound: 'Listing not found',
      backToListings: 'Back to listings',
      startingPrice: 'Starting price',
      bidsCount: 'bids',
      yourBid: 'Your bid (EUR)',
      minBid: 'Min.',
      submitBid: 'Submit bid',
      cancel: 'Cancel',
      showPhone: 'Show phone',
      sendEmail: 'Send email',
      published: 'Published',
      sendMessage: 'Send message',
      scheduleVisit: 'Schedule visit',
      similarListings: 'Similar listings',
      noMoreBids: 'No bids yet.',
      beFirst: 'Be the first!',
      perMonth: '/mo',
      plotArea: 'Plot',
    },
    filters: {
      title: 'Filters',
      category: 'Category',
      priceRange: 'Price Range',
      minPrice: 'Min price',
      maxPrice: 'Max price',
      areaRange: 'Area Range',
      minArea: 'Min area',
      maxArea: 'Max area',
      rooms: 'Rooms',
      anyRooms: 'Any number of rooms',
      heating: 'Heating',
      anyHeating: 'Any heating',
      features: 'Features',
      clearFilters: 'Clear filters',
      applyFilters: 'Apply filters',
      advancedFilters: 'Advanced filters',
      locationSearch: 'Location search',
      locationPlaceholder: 'Enter address or area...',
      selectAllRegions: 'Select all regions',
      deselectAllRegions: 'Deselect all regions',
    },
    categories: {
      house: 'House',
      apartment: 'Apartment',
      commercial: 'Commercial',
      land: 'Land',
      garage: 'Garage',
    },
    subscription: {
      title: 'Stay updated with the latest properties',
      subtitle: 'Subscribe to our newsletter and be the first to know about new listings matching your criteria.',
      emailPlaceholder: 'Your email address',
      subscribe: 'Subscribe',
      dismiss: 'No, thanks',
      success: 'Successfully subscribed to the newsletter!',
      alreadySubscribed: 'You are already subscribed to the newsletter.',
      invalidEmail: 'Please enter a valid email address.',
      privacyNote: 'Your data is safe and we will not share it with third parties.',
    },
    ai: {
      title: 'AI Assistant',
      subtitle: 'Ask us about prices, documentation and properties',
      placeholder: 'Ask a question about properties, regional prices, documentation...',
      send: 'Send',
      thinking: 'Thinking...',
      disclaimer: 'The AI assistant uses OpenAI to generate responses. Responses are informational and do not constitute legal advice.',
      legalDisclaimer: 'For legal advice and expert documentation, consult a lawyer or notary. More information at paragraf.rs',
      priceInfo: 'Average price in this region',
      documentCheck: 'Documents to check',
      suspiciousWarning: 'Potentially suspicious elements',
      prosAndCons: 'Pros and cons',
      reportAbuse: 'Report abuse',
      abuseReported: 'Report sent to administrator.',
    },
    blog: {
      title: 'Blog',
      subtitle: 'Useful tips and news from the real estate world',
      readMore: 'Read more',
      publishedOn: 'Published on',
      author: 'Author',
      categories: 'Categories',
      recentPosts: 'Recent Posts',
    },
    faq: {
      title: 'Frequently Asked Questions',
      subtitle: 'Find answers to the most common questions about using the platform',
      searchPlaceholder: 'Search questions...',
      noResults: 'No results for your search',
    },
    about: {
      title: 'About Us',
      subtitle: 'Svoj Na Svom was created by a dedicated and professional team with years of experience in marketing, real estate transactions, and information technology. We gathered user requirements, wishes, and needs to build the best website for advertising real estate needs.',
      mission: 'Our Mission',
      missionText: 'Connecting property buyers and sellers in a transparent, safe and efficient way. Feel free to contact us for more information at kancelarija@svojnasvome.com or +381 65 290 7002.',
      vision: 'Our Vision',
      visionText: 'To become the leading real estate platform in Serbia and the region, with the best user experience and advanced technological solutions.',
      values: 'Our Values',
      team: 'Our Team',
      contact: 'Contact Us',
    },
    auth: {
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm password',
      name: 'Full name',
      phone: 'Phone',
      loginTitle: 'Login',
      registerTitle: 'Register',
      forgotPassword: 'Forgot password?',
      noAccount: "Don't have an account?",
      hasAccount: 'Already have an account?',
      loginButton: 'Login',
      registerButton: 'Register',
      invalidCredentials: 'Invalid email or password',
      emailExists: 'Email address is already registered',
      passwordMismatch: 'Passwords do not match',
    },
    errors: {
      generic: 'An error occurred. Please try again.',
      network: 'Network error. Check your internet connection.',
      unauthorized: 'You do not have permission for this action.',
      notFound: 'Page not found.',
      validation: 'Please check your input.',
      serverError: 'Server error. Please try again later.',
    },
    notifications: {
      title: 'Notifications',
      noNotifications: 'No new notifications',
      markAsRead: 'Mark as read',
      newMatch: 'New match!',
      expiringListing: 'Listing expiring soon',
      newBid: 'New bid on your auction',
      outbid: 'You have been outbid',
    },
    footer: {
      description: 'Your trusted partner in finding the ideal home. Real estate advertising portal in Serbia.',
      links: 'Quick Links',
      legal: 'Legal Information',
      terms: 'Terms of Service',
      privacy: 'Privacy Policy',
      contact: 'Contact',
      copyright: '© 2026 Svoj Na Svom. All rights reserved.',
    },
    // Share
    share: {
      title: 'Share',
      copyLink: 'Copy link',
      copied: 'Copied!',
      shareVia: 'Share via',
      shareAnywhere: 'Share anywhere',
      copiesLink: 'Copies link',
    },
    // Demands
    demands: {
      title: 'Property Demands',
      subtitle: 'See what potential buyers and renters are looking for',
      haveProperty: 'Have a property that matches the demand?',
      contactDirectly: 'Contact the advertiser directly and offer your property',
      addListing: 'Add listing',
      noDemands: 'No demands match these filters',
      tryChanging: 'Try changing filters or post your own demand',
      postDemand: 'Post demand',
      buying: 'Buying',
      renting: 'Renting',
      budgetUpTo: 'Budget up to:',
      call: 'Call',
      sendMessage: 'Send message',
      upTo: 'Up to',
      rooms: 'rooms',
      onlyLoggedIn: 'Only logged-in users can see phone number',
      loginToSee: 'Login to see contact info',
    },
    // Auctions
    auctions: {
      title: 'Active Auctions',
      subtitle: 'Properties sold through auction',
      placeBid: 'Place Bid',
      currentBid: 'Current Bid',
      auctionEnds: 'Auction Ends',
      sendMessage: 'Send message',
      onlyVerified: 'Only verified users can place bids',
      verifyEmail: 'Please verify your email first',
    },
    // Contact visibility
    contact: {
      onlyLoggedIn: 'Only logged-in users can see contact information',
      onlyVerified: 'Only verified users can send messages and offers',
      loginRequired: 'Login required',
      verificationRequired: 'Email verification required',
    },
  },
};
